import { flowTypes, pageConfig } from '@/common/service/page-config';
import Flow from "@/flows/flow";
import { getCurrencySwitchAccountSubscriptions } from '@/common/service/base-paid';
import { plansDataMapper } from '@/common/service/plan-data-mapper';
import { VIEWS, ABANDON_CART_EMAIL_COOKIE_KEY, planTypes } from '@/common/constants';
import { parseSubscription2PlanList } from '@/common/service/parse';
import { preCheckForPbx } from '@/common/service/validate/pbx';
import { getQueryByLocationSearch, isEmptyVal } from '@/utils';
import { getPlanMap } from '@/apis';
import store from '@/store/index';
import { payMethodMap } from '@/views/checkout/payment/helper/constant';
import { getAudioCountriesParams, getTargetUserIdParam } from '@/common/service/flow/utils';
import { getOrderSummaryFields } from '@/flows/utils';
import { addParamsIfNeedUseVerification } from '@/common/service/checkout-utils';
import { regionTypesEnum } from '@/views/checkout/constant';
import { removeEmptyValue } from '@/common/service/checkout-utils';
import { cloneDeep } from 'lodash';
import { getPreAssignPhoneNumber, upgradeSuccessPage } from '@/common/service/common';
import { switchCurrency } from '@/apis/currency-switch';
import { pollingEventStatus } from '@/common/polling';
import globalEventBus from '@/common/global-event-bus';


const initShoppingCart = async () => {
    const queryParams = getQueryByLocationSearch();
    const targetCurrency = queryParams.currency;

    if (!targetCurrency) {
        throw new Error('Currency parameter is required for switch currency operation');
    }

    const subscriptions = await getCurrencySwitchAccountSubscriptions(targetCurrency, {
        isNeedCallPbx: false
    });

    const planMapRes = await getPlanMap();
    const allPlanMap = planMapRes.result;

    let planList = plansDataMapper(allPlanMap, subscriptions); 
    
    subscriptions.forEach(subscription => {
        const existingPlan = planList.find(plan =>
            plan.plan_code === subscription.plan_code ||
            plan.base_plan_code === subscription.plan_code
        );
        if (!existingPlan) {
            try {
                const parsedPlan = parseSubscription2PlanList(planList, subscription, store.getters.basicConfig);
                if (parsedPlan) {
                    if (parsedPlan.plan_code && parsedPlan.plan_code.includes('overage')) {
                        parsedPlan.capacity = subscription.participants || subscription.quantity || 0;
                        parsedPlan.participants = parsedPlan.capacity;
                    }
                    planList.push(parsedPlan);
                }
            } catch (error) {
                console.error('error', error);
            }
        }
    });
    
    planList = planList.map(plan => {
        const currencyPlan = plan?.plan_code && subscriptions.find(sub => {
            if (sub.plan_code === plan.plan_code) return true;
            // Match decorated plan codes (e.g., AIC prepay minute tiers "base$500") by base_plan_code
            if (plan.base_plan_code && sub.plan_code === plan.base_plan_code) {
                // AIC prepay: sub.quantity is the minutes count, match the exact tier
                return !plan.minutes || plan.minutes === sub.quantity;
            }
            return false;
        });
        if (currencyPlan) {
            // ZP AIC PAYG: subscription price is 0 (metered), keep unit price from plan map (same as pre.js)
            const isAicPayg = plan.type === planTypes.ZOOM_PHONE$AICONCIERGE && !plan.minutes;
            const newAmount = isAicPayg ? plan.amount : (currencyPlan.price ?? plan.amount);
            return {
                ...plan,
                amount: newAmount,
                ...(plan.is_prepay && plan.minutes ? { totalAmount: newAmount * plan.minutes } : {}),
                cartInfo: {
                    ...plan.cartInfo,
                    selected_number: (plan.type === planTypes.ZOOM_PHONE$AICONCIERGE && plan.minutes)
                        ? plan.minutes
                        : (currencyPlan.quantity || 1)
                }
            };
        }
        return plan;
    });
    
    // Filter out Custom Avatars payongo plans - they should not be displayed in currency switch cart
    planList = planList.filter(plan => !(plan.type === planTypes.CUSTOM_AVATARS && plan.isPayongo));

    //  force users to enter complete new payment method
    // Clear existing payment method info to show full payment form
    try {
        await store.dispatch('getPaymentBillingInfo');
        // Load payment gateway info including Adyen payment info for Google Pay/Apple Pay
        await store.dispatch('getPaymentGatewayInfo', {
            operation: 'switch_currency',
            currency: targetCurrency
        });
        
        // Reset billing info to appear as if user has no payment method
        store.commit('setBillingInfo', {
            ...store.getters.billingInfo,
            dfPaymentMethodId: null,
            paymentMethodType: null,
            dfPaymentMethod: null
        });
        

        store.commit('setPaymentMethodInfo', {
            paymentType: null,
            paymentGateway: null,
            paymentResponse: null
        });
        

        store.commit('setApmInfo', {
            isApm: false,
            apmPaymentMethodName: '',
            isDefaultPayment: false,
            defaultPaymentName: '',
            defaultWisePayment: '',
            tileImageFileName: '',
            invalidDueToCountryChange: false,
            blockSubmit: false
        });
        
        store.commit('setCvvReAuthInfo', {
            isCreditCardReAuthRequired: false,
            pmId: null,
            accountId: null,
            errorMessage: ''
        });
        
        // set initial payment method to credit card
        store.commit('setSelectedPaymentMethod', payMethodMap.credit);
        store.commit('setIsEditingPaymentMethod', true);
        
    } catch (error) {
        console.warn('Could not load billing info for currency switch:', error);
    }

    return {
        boughtPlans: [], 
        subscriptions,
        freeTrialPlans: [],
        allPlanMap,
        planMapFlat: planList, 
    };
};

const resolveView = () => {
    return VIEWS.COMPOUND; 
}

const preCheck = async () => {
    await preCheckForPbx();
    
    // Trigger address validation
    return new Promise((resolve, reject) => {
        if (!globalEventBus || !globalEventBus.$has('valid-address-form')) {
            return resolve();
        }
        
        globalEventBus.$emit('valid-address-form', { resolve, reject });
    });
}

const checkOutSubmit = async (submitParams) => {
    const preview = submitParams?.preview || false;
    
    if (preview) {
        return; 
    }
    
    const getters = store.getters;
    const { paymentMethodInfo, billToContact, soldToContact, taxInfo, addressInfo, account, isARLChecked, healthcareEligible, healthcareInfo } = getters;
    const { paymentResponse, paymentGateway } = paymentMethodInfo;
    
    const queryParams = getQueryByLocationSearch();
    const targetCurrency = queryParams.currency;
    
    const audioCountriesParams = getAudioCountriesParams(store);
    const targetUserIdParams = getTargetUserIdParam();
    const { businessCountry } = healthcareInfo || {};
    
    const currencySwitchParams = {
        taxInfo: taxInfo,
        contact: cloneDeep([
            removeEmptyValue(billToContact),
            removeEmptyValue(soldToContact)
        ]),
        paymentInfo: { 
            paymentResponse, 
            paymentGateway 
        },
        currency: targetCurrency,
        extension: {
            billingChannel: 'billing_page',
            usageType: addressInfo?.usageType,
            useCase: addressInfo?.useCase,
            otherUseCase: addressInfo?.otherUseCase,
            scheduledEmailId: window.$cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY),
            age: account?.age,
            ...(!audioCountriesParams ? {} : { tollFreeProfileData: audioCountriesParams }),
            ...(healthcareEligible ? { hipaaCompliant: true, baaCountry: businessCountry } : {}),
            ...(isARLChecked ? { isARLChecked: true } : {}),
            ...targetUserIdParams
        },
        agreements: {}
    };

    addParamsIfNeedUseVerification(currencySwitchParams.extension);

    if (getters.agreements && getters.agreements.regionTypes !== regionTypesEnum.NOLIMITED_REGIONS) {
        currencySwitchParams.agreements.isMarketingReview = !!getters.agreements.marketingReview;
    }

    const preAssignPhoneNumber = getPreAssignPhoneNumber();
    if (!isEmptyVal(preAssignPhoneNumber)) {
        currencySwitchParams.extension.preAssignPhoneNumber = preAssignPhoneNumber;
    }

    if (addressInfo?.isForEdu) {
        currencySwitchParams.extension.coppa = addressInfo.coppa.join(',');

        if (addressInfo?.usageType !== 'business' && !addressInfo?.companyName) {
            const { orgName = '', orgCount = '' } = addressInfo;
            currencySwitchParams.contact.forEach((c) => {
                c.companyName = orgName;
                c.employeeCount = orgCount;
            });
        }
    }

    currencySwitchParams.orderSummary = {
        orderSummaryFields: getOrderSummaryFields(getters),
    };

    if (pageConfig.mid) {
        currencySwitchParams.extension.meetingId = pageConfig.mid;
    }

    // Call the currency switch API
    await switchCurrency(currencySwitchParams);

    
    // Poll event status to ensure transaction completion
    const eventResult = await pollingEventStatus({ rps: true });
    store.commit('setTransactionId', eventResult?.uniqueKey);
    store.commit('setPreviewKey', eventResult?.previewKey);
    
    upgradeSuccessPage();
    
    return {
        eventResult,
    };
};

export default {
    [flowTypes.switchCurrency]: class extends Flow {
        view() { return resolveView() }
        init() { return initShoppingCart(...arguments) }
        beforeSubmit() { return preCheck() }
        submit(submitParams) { return checkOutSubmit(submitParams) }
    }
};
